/**
 * Soft≪AA isolation flags — probe-only toggles to separate fullheal vs dual-seat.
 * Default = Soft-safe WT floor (pre-regress). Claim B chase never enabled here.
 *
 * Soft stack (`SoftStackProbe`) = party survival **floor**, not a sell trait.
 * Never map softStackSurviveSpeedMul(1.22) onto instinct.
 */
export type SoftAaDualSeatGate = "charge_and_pack" | "living_pack";
export type SoftAaDualSeatAlgo = "first_clear" | "tournament";

export type SoftAaIsolateFlags = {
  /** Healer triage threshold — 0.72 Soft-safe WT; 1.0 = fullheal (main Soft≪AA). */
  healBelowPct: number;
  /**
   * When dual-seat packChew path opens.
   * `charge_and_pack` = charge∩pack **or** reactive feet-InAa (after pack-escape miss).
   * `living_pack` = any living PackChew (main Soft≪AA; often regresses).
   */
  dualSeatGate: SoftAaDualSeatGate;
  /** How candidates are picked once gate opens. */
  dualSeatAlgo: SoftAaDualSeatAlgo;
  /**
   * Soft≪AA-safe narrow hard filter: while feet InAa, among DZ-clear seats
   * prefer only healer-staff-reachable if any exist. Not a pull; not broad
   * seatFilterScore (that Soft-regressed N20 0.6).
   */
  narrowStaffHardFilterInAa: boolean;
  /**
   * Sell Soft assist (통찰): healer feet clear + ally chewed in-staff →
   * prefer that ally among near-lowest HP (ε). Not OOR chase; not always-on
   * chew triage (Soft 0.85 reject).
   */
  insightChewTriageInStaff: boolean;
  /**
   * EncounterKnowledge (통찰): packChew living + charge corridor open →
   * widen Soft foresight near-approach. Tag-based; no combat cheats.
   */
  encounterPackChargeForesight: boolean;
  /**
   * Option A 생존 추구 Soft-safe tip (default ON for Soft-playable).
   * Scorer: `survivalSeekScoreBonus` — 직감 gate + 통찰 preference.
   * Not `instinctAllowsCoverSeek` alone.
   */
  survivalSeekScorer: boolean;
};

/** Soft-safe WT plateau — fullheal + charge∩pack|InAa-reactive + first_clear. */
export const SOFT_AA_SAFE_FLAGS: SoftAaIsolateFlags = {
  healBelowPct: 1.0,
  dualSeatGate: "charge_and_pack",
  dualSeatAlgo: "first_clear",
  narrowStaffHardFilterInAa: false,
  insightChewTriageInStaff: false,
  encounterPackChargeForesight: false,
  survivalSeekScorer: true,
};

/** Main Soft≪AA combo that regressed this WT (tournament + living gate). */
export const SOFT_AA_MAIN_PORT_FLAGS: SoftAaIsolateFlags = {
  healBelowPct: 1.0,
  dualSeatGate: "living_pack",
  dualSeatAlgo: "tournament",
  narrowStaffHardFilterInAa: false,
  insightChewTriageInStaff: false,
  encounterPackChargeForesight: false,
  survivalSeekScorer: true,
};

/**
 * Soft stack probe — party survival **floor** (not a sell trait).
 * Dials baked Soft intensity without new geometry. Default `ship` = WT Soft
 * 0.2 stack. Never map these muls onto instinct/insight/bond sliders.
 * Claim S ladder / Soft≪AA isolate only — not Judgment Selling axes.
 */
export type SoftStackProbe = "off" | "mild" | "ship";

let softStackProbe: SoftStackProbe = "ship";

export function getSoftStackProbe(): SoftStackProbe {
  return softStackProbe;
}

export function setSoftStackProbe(stack: SoftStackProbe): void {
  softStackProbe = stack;
}

export function resetSoftStackProbe(): void {
  softStackProbe = "ship";
}

/** Soft-stack floor: multi-thrall rose step mul (off=1.6, mild/ship=2.2). Not a trait. */
export function softStackMultiRoseMul(): number {
  return softStackProbe === "off" ? 1.6 : 2.2;
}

/** Soft-stack floor: thrallChew survive speedMul (ship=1.22). Not instinct. */
export function softStackSurviveSpeedMul(): number {
  return softStackProbe === "ship" ? 1.22 : 1;
}

let flags: SoftAaIsolateFlags = { ...SOFT_AA_SAFE_FLAGS };

export function getSoftAaIsolateFlags(): SoftAaIsolateFlags {
  return flags;
}

export function setSoftAaIsolateFlags(
  partial: Partial<SoftAaIsolateFlags>,
): void {
  flags = { ...flags, ...partial };
}

export function resetSoftAaIsolateFlags(): void {
  flags = { ...SOFT_AA_SAFE_FLAGS };
}

/** Effective heal belowPct (isolate override wins over catalog). */
export function softAaHealBelowPct(catalogPct: number): number {
  void catalogPct;
  return flags.healBelowPct;
}
